import { defineStore } from 'pinia'
import { store } from '@/store'
import { reqGetMusicCreationTags } from '@/api/music/musicApi'
import { CommonUtils } from '@/utils/commonUtils'

interface Tag {
  value: string
  name: string
}

interface TagCategory {
  category: string
  tags: Tag[]
}

interface TagMapType {
  [key: string]: string
}

export const useMusicTagsStore = defineStore('musicTags', {
  state: () => ({
    tagCategories: [] as TagCategory[],
    tagMap: {} as TagMapType,
    isLoading: false,
    isInitialized: false,
    lastFetchTime: 0, // 上次获取数据的时间戳
    tagsCache: {} as Record<string, string[]>, // 添加缓存对象存储已处理的标签
  }),

  actions: {
    // 获取音乐创作标签
    async fetchMusicTags() {
      // 如果已加载且距离上次加载时间不超过1小时，直接返回数据
      const ONE_HOUR = 60 * 60 * 1000
      const now = Date.now()
      if (this.isInitialized && now - this.lastFetchTime < ONE_HOUR) {
        return this.tagCategories
      }

      // 如果正在加载中，直接返回现有数据
      if (this.isLoading) {
        return this.tagCategories
      }

      this.isLoading = true
      try {
        const res = await reqGetMusicCreationTags({
          lang: CommonUtils.getLang(),
        })
        this.setTagCategories(res.data)
        this.isInitialized = true
        this.lastFetchTime = now
        return res.data
      } catch (error) {
        console.error('获取音乐标签失败:', error)
        return []
      } finally {
        this.isLoading = false
      }
    },

    // 设置标签数据
    setTagCategories(categories: TagCategory[]) {
      this.tagCategories = categories

      // 构建扁平化映射，用于快速查找
      const map: TagMapType = {}
      categories.forEach((category) => {
        category.tags.forEach((tag) => {
          map[tag.value] = tag.name
        })
      })
      this.tagMap = map

      // 清除标签缓存，确保数据一致性
      this.clearTagsCache()
    },

    // 根据value查找name
    getTagNameByValue(value: string): string {
      return this.tagMap[value] || value
    },

    // 根据多个value查找多个name
    getTagNamesByValues(values: string[]): string[] {
      return values.map((value) => this.getTagNameByValue(value))
    },

    // 获取所有标签（扁平化）
    getAllTags(): Tag[] {
      const allTags: Tag[] = []
      this.tagCategories.forEach((category) => {
        allTags.push(...category.tags)
      })
      return allTags
    },

    // 根据多个value查找对应的Tag对象
    getTagsByValues(values: string[]): Tag[] {
      const allTags = this.getAllTags()
      return values
        .map((value) => allTags.find((tag) => tag.value === value))
        .filter((tag): tag is Tag => tag !== undefined)
    },

    // 格式化标签字符串为标签名称数组
    formatTags(tagsString: string): string[] {
      if (!tagsString) return []

      // 检查缓存中是否已存在结果
      if (this.tagsCache[tagsString]) {
        return this.tagsCache[tagsString]
      }

      // 同时处理中文逗号和英文逗号
      const tagValues = tagsString
        .replace(/，/g, ',')
        .split(',')
        .filter((tag) => tag.trim() !== '')

      // 使用映射获取标签名称
      const result = this.getTagNamesByValues(tagValues)

      // 将结果存入缓存
      this.tagsCache[tagsString] = result

      return result
    },

    // 清除标签缓存
    clearTagsCache() {
      this.tagsCache = {}
    },
  },
})

export function useMusicTagsStoreHook() {
  return useMusicTagsStore(store)
}
